/**
 * Orders list data access, shared by both apps.
 */
import { apiFetch } from './api'

export interface OrderItemSummary {
  id: string
  product_code: string
  product_name: string | null
  status: string
}

export interface OrderSummary {
  id: string
  project_id: string | null
  status: string
  visibility?: string
  total_cents: number
  created_at: string | null
  items: OrderItemSummary[]
}

/** One line item in a create-order request. */
export interface CreateOrderItemInput {
  product_type: string
  polygon: GeoJSON.Polygon
  area_ha: number
  price_cents: number
}

export interface CreateOrderInput {
  items: CreateOrderItemInput[]
  total_cents: number
  /** Optional project this order belongs to (when ordered from a project). */
  project_id?: string
}

/** Minimal shape returned by POST /orders (enough to navigate to the order). */
export interface CreatedOrder {
  id: string
  status: string
  project_id?: string | null
  total_cents?: number
}

export const ordersService = {
  /**
   * Create an order with its line items. Mirrors the analyzer POST /orders
   * contract used by the frontend cart; floors total_cents defensively.
   */
  async createOrder(
    input: CreateOrderInput,
  ): Promise<{ order: CreatedOrder | null; error: Error | null }> {
    const body: Record<string, unknown> = {
      items: input.items,
      total_cents: Math.floor(input.total_cents),
    }
    if (input.project_id) body.project_id = input.project_id
    const { data, error } = await apiFetch<CreatedOrder>('/orders', {
      method: 'POST',
      body: JSON.stringify(body),
    })
    return { order: data, error }
  },

  /**
   * List the current user's orders (staff see all). Tolerates either a bare
   * array or a { orders: [...] } envelope from the API.
   */
  async getOrders(): Promise<{ orders: OrderSummary[]; error: Error | null }> {
    const { data, error } = await apiFetch<unknown>('/orders')
    let orders: OrderSummary[] = []
    if (Array.isArray(data)) orders = data as OrderSummary[]
    else if (data && typeof data === 'object' && Array.isArray((data as { orders?: unknown }).orders)) {
      orders = (data as { orders: OrderSummary[] }).orders
    }
    return { orders, error }
  },
}
